import { AssetProgressDto } from "@core/typesV2";
import { useUpdateAssetsMutation } from "@providersV2/useUpdateAssetsMutation";
import { useCallback, useEffect, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";

type PendingUpdate = AssetProgressDto;

export function useDebouncedAssetProgressSaver({
  courseId,
  activityId,
}: {
  courseId: string;
  activityId: string;
}) {
  const { mutateAsync } = useUpdateAssetsMutation();

  const pendingRef = useRef<Map<string, PendingUpdate>>(new Map());
  const inFlightRef = useRef<Promise<void> | null>(null);

  const flush = useCallback(async () => {
    if (inFlightRef.current) {
      await inFlightRef.current;
    }

    const updates = Array.from(pendingRef.current.values());
    if (!updates.length) return;

    pendingRef.current.clear();

    const p = (async () => {
      try {
        await mutateAsync({
          courseId,
          activityId,
          assetProgressDtos: updates,
        });
      } catch (e) {
        for (const u of updates) {
          if (!pendingRef.current.has(u.assetId)) {
            pendingRef.current.set(u.assetId, u);
          }
        }
        throw e;
      } finally {
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = p;
    await p;
  }, [mutateAsync, courseId, activityId]);

  const debouncedFlush = useDebouncedCallback(() => {
    void flush();
  }, 3000);

  useEffect(
    () => () => {
      debouncedFlush.cancel();
      void flush();
    },
    [debouncedFlush, flush],
  );

  const debounceSaveResponse = useCallback(
    (assetId: string, response: unknown, visitedOn?: string) => {
      const existing = pendingRef.current.get(assetId);

      pendingRef.current.set(assetId, {
        assetId,
        response:
          response === undefined ? existing?.response : { value: response },
        visitedOn: existing?.visitedOn ?? visitedOn,
      });

      // A null response is an explicit "clear this answer" (YouPick option
      // switch) — persist it now so a refresh can't race the debounce.
      if (response === null) {
        debouncedFlush.cancel();
        void flush();
      } else {
        debouncedFlush();
      }
    },
    [debouncedFlush, flush],
  );

  const flushChanges = useCallback(async () => {
    debouncedFlush.cancel();
    await flush();
  }, [debouncedFlush, flush]);

  return { debounceSaveResponse, flushChanges };
}
